<app-menu>
  <div class="asn-shell" [attr.data-theme]="themeMode">
    <div class="asn-wrapper mx-auto flex min-h-0 max-w-[1900px] flex-col gap-3 px-4 py-3" [class.has-import-scope]="parsedFiles.length">
      <header class="asn-toolbar">
        <div class="asn-title-stack min-w-0">
          <div class="asn-eyebrow">Admin Tools / SFTP Intake</div>
          <div class="flex min-w-0 flex-wrap items-center gap-3">
            <h1 class="truncate">Spencer ASN Importer</h1>
            <span class="asn-readiness-pill" [ngClass]="readinessClass()">
              <i class="fa-solid" [ngClass]="readinessTone === 'error' ? 'fa-circle-xmark' : readinessTone === 'warning' ? 'fa-triangle-exclamation' : readinessTone === 'ready' ? 'fa-circle-check' : 'fa-circle-info'"></i>
              {{ readinessLabel }}
            </span>
          </div>
          <div class="asn-meta-strip">
            <span><strong>{{ files.length }}</strong> active CSVs</span>
            <span><strong>{{ archiveCount }}</strong> archived</span>
            <span><strong>{{ selectedCount }}</strong> selected</span>
            <span><strong>{{ formatBytes(selectedFileBytes) }}</strong> selected size</span>
            <span *ngIf="listGeneratedAt">Loaded {{ formatDate(listGeneratedAt) }}</span>
          </div>
        </div>

        <div class="asn-toolbar-actions">
          <button type="button" class="asn-button asn-button--ghost" (click)="toggleTheme()" [title]="getThemeLabel() + ' theme'">
            <i [class]="getThemeIcon()"></i>
            <span>{{ getThemeLabel() }}</span>
          </button>
          <button type="button" class="asn-button asn-button--ghost" (click)="loadFiles()" [disabled]="loadingFiles || previewing || downloading || archiving || processing">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loadingFiles"></i>
            <span>{{ loadingFiles ? 'Loading' : 'Refresh' }}</span>
          </button>
          <button type="button" class="asn-button asn-button--ghost" (click)="downloadSelectedFiles()" [disabled]="!selectedCount || loadingFiles || previewing || downloading || archiving || processing" [title]="isArchiveMode ? 'Download selected archived CSV files' : 'Download selected CSV files'">
            <i class="fa-solid fa-download" [class.fa-beat-fade]="downloading"></i>
            <span>{{ downloading ? 'Downloading' : 'Download' }}</span>
          </button>
          <button type="button" class="asn-button asn-button--warning" (click)="archiveSelectedFiles()" [disabled]="!canArchiveSelected || loadingFiles || previewing || downloading || archiving || processing" [title]="archiveButtonTitle">
            <i class="fa-solid fa-box-archive" [class.fa-beat-fade]="archiving"></i>
            <span>{{ archiving ? 'Archiving' : 'Archive' }}</span>
          </button>
          <button type="button" class="asn-button asn-button--primary" (click)="previewSelected()" [disabled]="!selectedCount || spiritPreviewChoiceMissingCount > 0 || loadingFiles || previewing || downloading || archiving || processing" [title]="previewButtonTitle">
            <i class="fa-solid fa-eye" [class.fa-beat-fade]="previewing"></i>
            <span>{{ previewing ? 'Previewing' : 'Preview' }}</span>
          </button>
          <button type="button" class="asn-button asn-button--accent" (click)="processSelected()" [disabled]="!canRunFinalProcess || loadingFiles || previewing || downloading || archiving || processing" [title]="processDisabledReason">
            <i [class]="processButtonIcon" [class.fa-beat-fade]="processing"></i>
            <span>{{ processButtonLabel }}</span>
          </button>
        </div>
      </header>

      <section class="asn-envbar" *ngIf="config">
        <span><i class="fa-solid fa-server"></i><strong>Host</strong>{{ config['host'] }}</span>
        <span><i class="fa-solid fa-folder-tree"></i><strong>Directory</strong>{{ config['directory'] }}</span>
        <span><i class="fa-solid fa-box-archive"></i><strong>Archive</strong>{{ config['archive_directory'] }}</span>
      </section>

      <section class="asn-optionbar">
        <div class="asn-option-toggle is-enabled">
          <i class="fa-solid fa-diagram-project"></i>
          <span class="asn-option-copy">
            <span class="asn-option-heading">
              <strong>Automatic shipment grouping</strong>
              <b>Required</b>
            </span>
            <small>New Master BOLs create one trip each. Barcode matches can identify an existing blind trip for reconciliation; review the differences before merging. Shipments are grouped by store + STORE_BOL, with source order numbers retained.</small>
          </span>
          <span class="asn-option-state">Always on</span>
        </div>
        <div class="asn-option-toggle" [class.is-enabled]="ignoreRowsWithoutMasterBol">
          <i class="fa-solid fa-filter-circle-xmark"></i>
          <span class="asn-option-copy">
            <span class="asn-option-heading">
              <strong>Skip rows without Master BOL</strong>
              <b>Optional</b>
            </span>
            <small>Rows with a blank master_bol_number are removed before validation and processing. They are never written or included in barcode checks.</small>
          </span>
          <label class="asn-check-label">
            <input type="checkbox" class="asn-check" [(ngModel)]="ignoreRowsWithoutMasterBol">
            <span>{{ ignoreRowsWithoutMasterBol ? 'On' : 'Off' }}</span>
          </label>
        </div>

        <div class="asn-option-toggle" [class.is-enabled]="ignoreExistingMasterBol">
          <i class="fa-solid fa-ban"></i>
          <span class="asn-option-copy">
            <span class="asn-option-heading">
              <strong>Skip already-used Master BOL groups</strong>
              <b>Optional</b>
            </span>
            <small>An existing Master BOL removes its entire group and carton rows before validation and processing. Other Master BOLs continue normally.</small>
          </span>
          <label class="asn-check-label">
            <input type="checkbox" class="asn-check" [(ngModel)]="ignoreExistingMasterBol">
            <span>{{ ignoreExistingMasterBol ? 'On' : 'Off' }}</span>
          </label>
        </div>
      </section>

      <section class="asn-spirit-selector" *ngIf="parsedFiles.length">
        <div class="asn-spirit-selector-head">
          <div>
            <div class="asn-eyebrow">Import scope</div>
            <h2>Spirit and mixed-load selection</h2>
            <p>Standalone Spirit loads are optional. When Spirit shares a Master BOL with Spencer, both portions are required and process together as one trip.</p>
          </div>
          <div class="asn-spirit-selector-summary">
            <span><strong>{{ requiredMixedMasterBolCount }}</strong> mixed required</span>
            <span><strong>{{ selectedOptionalSpiritMasterBolCount }}</strong> standalone selected</span>
            <span><strong>{{ unselectedOptionalSpiritMasterBolCount }}</strong> standalone skipped</span>
            <b>Mixed is atomic</b>
          </div>
        </div>

        <div class="asn-spirit-safety">
          <i class="fa-solid fa-shield-halved"></i>
          <span>Selections are keyed by CSV filename and Master BOL. A mixed Master BOL cannot import only its Spencer half. The UI does not inspect KO barcode prefixes or create/change database pool mappings.</span>
        </div>

        <div class="asn-spirit-file-list">
          <article class="asn-spirit-file" *ngFor="let file of spiritCandidateFiles; trackBy: trackByFilename">
            <header>
              <div class="min-w-0">
                <strong class="truncate">{{ file.filename }}</strong>
                <small>{{ file.candidates.length }} Spirit or mixed Master BOL{{ file.candidates.length === 1 ? '' : 's' }}</small>
              </div>
              <label class="asn-check-label" [class.is-disabled]="!spiritOptionalCountFor(file) || spiritSelectionBusy">
                <input
                  type="checkbox"
                  class="asn-check"
                  [checked]="spiritAllSelected(file)"
                  [indeterminate]="spiritSomeSelected(file)"
                  (change)="toggleSpiritCandidatesForFile(file, $any($event.target).checked)"
                  [disabled]="!spiritOptionalCountFor(file) || spiritSelectionBusy">
                <span>All optional</span>
              </label>
            </header>

            <div class="asn-spirit-candidate-list" *ngIf="file.candidates.length; else noSpiritCandidates">
              <div class="asn-spirit-candidate" *ngFor="let candidate of file.candidates; trackBy: trackBySpiritMasterBol" [class.is-selected]="candidate.selected" [class.is-required]="candidate.required">
                <input
                  type="checkbox"
                  class="asn-check"
                  [(ngModel)]="candidate.selected"
                  (ngModelChange)="onSpiritSelectionChanged()"
                  [disabled]="candidate.required || spiritSelectionBusy"
                  [title]="candidate.required ? 'Required because this Master BOL also contains Spencer rows' : 'Include this standalone Spirit Master BOL'">
                <span class="asn-spirit-candidate-main">
                  <strong>{{ candidate.master_bol_number }}</strong>
                  <small>{{ candidate.required ? 'Mixed Master BOL' : 'Spirit Master BOL' }}</small>
                  <b class="asn-mixed-required" *ngIf="candidate.required">
                    <i class="fa-solid fa-link"></i>
                    Mixed — {{ mixedCandidatePoolLabel(candidate) }} · required
                  </b>
                </span>
                <span>
                  <small>CSV source pool</small>
                  <strong>{{ candidate.source_pool }}</strong>
                  <b class="asn-spirit-source-warning" *ngIf="candidate.pool_override_reason === 'customer_pool_mismatch'">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    Not valid for Spirit
                  </b>
                </span>
                <span class="asn-spirit-pool-choice">
                  <small>Effective replenishment pool</small>
                  <select
                    *ngIf="spiritPoolPickerVisible(candidate); else sourcePoolValue"
                    [(ngModel)]="candidate.pool_override"
                    (ngModelChange)="onSpiritPoolOverrideChanged(candidate)"
                    [disabled]="spiritSelectionBusy || loadingSpiritPoolOptions">
                    <option value="">{{ candidate.pool_override_reason === 'customer_pool_mismatch' ? 'Select corrected pool…' : 'Select pool…' }}</option>
                    <option *ngFor="let pool of spiritPoolOptionsFor(candidate)" [value]="pool.pool_point_client_id">
                      {{ pool.pool_point_client_id }} — {{ pool.pool_point_name || pool.pool_point_client_name }}
                    </option>
                  </select>
                  <ng-template #sourcePoolValue><strong>{{ candidate.source_pool }}</strong></ng-template>
                  <b class="asn-spirit-pool-ack" *ngIf="candidate.preview_effective_pool">
                    <i class="fa-solid fa-circle-check"></i>
                    {{ isSpiritPoolCorrection(candidate) ? 'Preview confirmed correction ' + spiritPoolCorrectionLabel(candidate) : 'Preview accepted ' + candidate.preview_effective_pool }}
                  </b>
                  <b class="asn-spirit-pool-pending" *ngIf="candidate.selected && spiritPoolPickerVisible(candidate) && candidate.pool_override && !candidate.preview_effective_pool">
                    Preview required to confirm {{ spiritPoolCorrectionLabel(candidate) }}
                  </b>
                </span>
                <span><small>Warehouse</small><strong>{{ candidate.warehouse }}</strong></span>
                <span><small>Orders</small><strong>{{ candidate.order_count }}</strong></span>
                <span><small>Cartons</small><strong>{{ candidate.carton_count }}</strong></span>
                <label
                  class="asn-spirit-out-of-market"
                  [class.is-confirmed]="candidate.preview_allow_unmapped_stores"
                  *ngIf="candidate.selected && isSpiritPoolCorrection(candidate)">
                  <input
                    type="checkbox"
                    class="asn-check"
                    [(ngModel)]="candidate.allow_unmapped_stores"
                    (ngModelChange)="onSpiritUnmappedAcknowledgementChanged()"
                    [disabled]="spiritSelectionBusy">
                  <span>
                    <strong>Include these cartons as held/out-of-market candidates</strong>
                    <small>Keep every ASN barcode and store number. A store without a mapping at the selected pool remains OUT OF MARKET / HELD FOR DISPOSITION; this does not move the store into that pool.</small>
                  </span>
                  <b *ngIf="candidate.preview_allow_unmapped_stores">
                    <i class="fa-solid fa-circle-check"></i>
                    Preview confirmed
                  </b>
                </label>
              </div>
            </div>

            <ng-template #noSpiritCandidates>
              <div class="asn-spirit-empty">No Spirit Master BOLs found in this CSV.</div>
            </ng-template>
          </article>
        </div>

        <div class="asn-spirit-archive-warning">
          <i class="fa-solid fa-box-archive"></i>
          <span><strong>Whole-file archive:</strong> unselected standalone Spirit rows are ignored. Mixed Spencer + Spirit Master BOLs always process together. After a successful Process, the whole CSV is archived, so ignored rows will not remain active for a later run.</span>
        </div>
      </section>

      <div class="asn-alert asn-alert--error" *ngIf="errorMessage">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <main class="asn-work-grid min-h-0">
        <aside class="asn-panel asn-file-panel min-h-0">
          <div class="asn-panel-header">
            <div>
              <div class="asn-eyebrow">SFTP</div>
              <h2>{{ isArchiveMode ? 'Archive' : 'Files' }}</h2>
            </div>
            <div class="asn-file-panel-actions">
              <div class="asn-file-mode-toggle">
                <button type="button" [class.is-active]="fileMode === 'active'" (click)="setFileMode('active')">
                  <i class="fa-solid fa-inbox"></i>
                  <span>Active</span>
                  <strong>{{ files.length }}</strong>
                </button>
                <button type="button" [class.is-active]="fileMode === 'archive'" (click)="setFileMode('archive')">
                  <i class="fa-solid fa-box-archive"></i>
                  <span>Archive</span>
                  <strong>{{ archiveCount }}</strong>
                </button>
              </div>
              <label class="asn-check-label" [class.is-disabled]="!visibleFileCount || loadingFiles || archiving || processing">
                <input type="checkbox" class="asn-check" [checked]="allSelected" [indeterminate]="selectedCount > 0 && !allSelected" (change)="toggleAll($any($event.target).checked)" [disabled]="!visibleFileCount || loadingFiles || archiving || processing">
                <span>All</span>
              </label>
            </div>
          </div>

          <div class="asn-file-list" *ngIf="visibleFileCount; else noFiles">
            <label class="asn-file-row" *ngFor="let file of visibleFiles; trackBy: trackByFilename" [class.is-selected]="file.selected" [class.is-archived]="isArchiveMode">
              <input type="checkbox" class="asn-check" [(ngModel)]="file.selected" [disabled]="previewing || archiving || processing">
              <span class="asn-file-main">
                <span class="asn-file-name">{{ file.filename }}</span>
                <span class="asn-file-subline">
                  <span class="asn-file-sub">{{ formatBytes(file.size) }} / {{ formatDate(file.modified_at) }}</span>
                  <span class="asn-file-bol-count" *ngIf="masterBolCountLabel(file.filename) as bolLabel">{{ bolLabel }}</span>
                </span>
              </span>
              <span class="asn-file-mark" *ngIf="file.selected"><i class="fa-solid fa-check"></i></span>
            </label>
          </div>

          <ng-template #noFiles>
            <div class="asn-empty">
              <i class="fa-solid" [ngClass]="isArchiveMode ? 'fa-box-archive' : 'fa-folder-open'"></i>
              <span>{{ isArchiveMode ? 'No archived CSV files found.' : 'No active CSV files found.' }}</span>
            </div>
          </ng-template>

          <section class="asn-readiness-card" [ngClass]="readinessClass()">
            <div class="asn-readiness-head">
              <div>
                <span>Readiness</span>
                <strong>{{ readinessLabel }}</strong>
              </div>
              <b>{{ readinessPercent }}%</b>
            </div>
            <div class="asn-progress">
              <span [style.width.%]="readinessPercent"></span>
            </div>
            <div class="asn-readiness-grid">
              <span><strong>{{ totalGroupCount }}</strong> groups</span>
              <span><strong>{{ readyGroupCount }}</strong> ready</span>
              <span><strong>{{ blockingPreviewCount }}</strong> blocked</span>
              <span><strong>{{ warningGroupCount }}</strong> warnings</span>
            </div>
            <p>{{ processDisabledReason }}</p>
          </section>

          <section class="asn-issues-card">
            <div class="asn-issues-head">
              <span>Data Checks</span>
              <strong>{{ issues.length }}</strong>
            </div>
            <div class="asn-issue-list" *ngIf="issues.length; else cleanIssues">
              <article class="asn-issue" *ngFor="let issue of visibleIssues; trackBy: trackByIssue" [ngClass]="issueClass(issue)">
                <i class="fa-solid" [ngClass]="issue.severity === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'"></i>
                <div>
                  <strong>{{ issue.master_bol_number || issue.filename }}</strong>
                  <span>{{ issue.message }}</span>
                </div>
              </article>
              <div class="asn-more-issues" *ngIf="issues.length > visibleIssues.length">{{ issues.length - visibleIssues.length }} more issue{{ issues.length - visibleIssues.length === 1 ? '' : 's' }}</div>
            </div>
            <ng-template #cleanIssues>
              <div class="asn-clean-state">
                <i class="fa-solid fa-circle-check"></i>
                <span>{{ previewFiles.length ? 'No validation issues in this preview.' : 'Preview selected files to run checks.' }}</span>
              </div>
            </ng-template>
          </section>
        </aside>

        <section class="asn-panel asn-workbench min-h-0">
          <div class="asn-tabs">
            <button type="button" class="asn-tab" [class.is-active]="activeTab === 'mapping'" (click)="setActiveTab('mapping')">
              <i class="fa-solid fa-diagram-project"></i>
              <span>Mapping</span>
            </button>
            <button type="button" class="asn-tab" [class.is-active]="activeTab === 'rows'" (click)="setActiveTab('rows')">
              <i class="fa-solid fa-table-cells"></i>
              <span>Rows</span>
            </button>
            <button type="button" class="asn-tab" [class.is-active]="activeTab === 'json'" (click)="setActiveTab('json')">
              <i class="fa-solid fa-code"></i>
              <span>JSON</span>
            </button>
            <button type="button" class="asn-tab" [class.is-active]="activeTab === 'results'" (click)="setActiveTab('results')">
              <i class="fa-solid fa-list-check"></i>
              <span>Results</span>
            </button>
          </div>

          <div class="asn-workbench-body" [ngSwitch]="activeTab">
            <section *ngSwitchCase="'mapping'" class="asn-tab-panel">
              <div class="asn-preview-strip" *ngIf="previewFiles.length">
                <button type="button" class="asn-preview-chip" *ngFor="let file of previewFiles; trackBy: trackByFilename" (click)="selectPreviewFile(file.filename)" [class.is-active]="selectedPreview?.filename === file.filename">
                  <span>{{ file.filename }}</span>
                  <small>{{ masterBolCountLabel(file.filename) }}</small>
                  <strong [ngClass]="statusClass(file.status)">{{ file.status }}</strong>
                </button>
              </div>

              <div class="asn-alert asn-alert--warning" *ngIf="previewFiles.length && !previewMatchesSelection">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ previewOutOfSyncMessage }} Run Preview again before processing.</span>
              </div>

              <ng-container *ngIf="selectedPreview as preview; else noPreview">
                <div class="asn-preview-head">
                  <div class="min-w-0">
                    <div class="asn-eyebrow">Selected Preview</div>
                    <h2 class="truncate">{{ preview.filename }}</h2>
                    <p>{{ preview.message }}</p>
                  </div>
                  <span class="asn-status-pill" [ngClass]="statusClass(preview.status)">{{ preview.status }}</span>
                </div>

                <div class="asn-count-row" *ngIf="preview.row_counts">
                  <span *ngFor="let key of objectKeys(preview.row_counts)">
                    <strong>{{ $any(preview.row_counts)[key] }}</strong>
                    {{ labelize(key) }}
                  </span>
                </div>

                <div class="asn-raw-bol-strip" *ngIf="rawMasterBolSummaries.length">
                  <button type="button" class="asn-raw-bol-chip" *ngFor="let summary of rawMasterBolSummaries; trackBy: trackByRawMasterBol" [ngClass]="rawMasterBolClass(summary)" (click)="viewRawMasterBol(summary.master_bol_number)" [title]="rawMasterBolTitle(summary)">
                    <strong>{{ summary.master_bol_number }}</strong>
                    <span>{{ summary.rows }} rows</span>
                    <small>{{ summary.spencer_rows }} Spencer / {{ summary.spirit_rows }} Spirit / {{ summary.other_rows }} Other</small>
                  </button>
                </div>

                <article class="asn-group-card" *ngFor="let group of preview.groups; trackBy: trackByMasterBol">
                  <header class="asn-group-head" [class.asn-group-head--mixed]="isMixedGroup(group)">
                    <div class="min-w-0">
                      <div class="asn-eyebrow">{{ isMixedGroup(group) ? 'Mixed physical trip' : 'Master BOL' }}</div>
                      <h3 class="truncate">{{ group.master_bol_number }}</h3>
                    </div>
                    <div class="asn-group-status-stack">
                      <b class="asn-mixed-pill" *ngIf="isMixedGroup(group)">
                        <i class="fa-solid fa-link"></i>
                        {{ groupCustomerLabel(group) }}
                      </b>
                      <span class="asn-status-pill" [ngClass]="statusClass(group.status)">{{ group.status }}</span>
                    </div>
                  </header>

                  <div class="asn-group-facts">
                    <span><small>Customer</small><strong>{{ groupCustomerLabel(group) }}</strong></span>
                    <span><small>Reference</small><strong>{{ group.warehouse_external_reference }}</strong></span>
                    <span><small>Source pools</small><strong>{{ groupPoolLabel(group) }}</strong></span>
                    <span><small>Warehouse</small><strong>{{ groupWarehouseLabel(group) }}</strong></span>
                    <span><small>Week</small><strong>{{ group.week_no }}</strong></span>
                    <span><small>Stores</small><strong>{{ group.store_count }}</strong></span>
                    <span><small>Cartons</small><strong>{{ group.cartons }}</strong></span>
                    <span><small>Weight</small><strong>{{ group.weight }}</strong></span>
                  </div>

                  <section class="asn-mixed-load" *ngIf="isMixedGroup(group)">
                    <div class="asn-mixed-load-head">
                      <div>
                        <span>Atomic mixed load</span>
                        <strong>One Master BOL · one trip · both customers</strong>
                      </div>
                      <b>Cannot split</b>
                    </div>
                    <p><i class="fa-solid fa-shield-halved"></i> Spencer and Spirit remain separate on their shipment and product rows, but this physical load is previewed and processed together.</p>
                    <div class="asn-mixed-segments" *ngIf="group.segments?.length">
                      <article class="asn-mixed-segment" *ngFor="let segment of group.segments; trackBy: trackBySegment">
                        <header>
                          <div>
                            <b>{{ segment.pool_point || (segment.customer_kind === 'spirit' ? 'FB05' : 'FB01') }}</b>
                            <strong>{{ segment.customer_name || customerKindLabel(segment.customer_kind) }}</strong>
                          </div>
                          <span>{{ segment.cartons }} cartons · {{ segment.store_count }} stores</span>
                        </header>
                        <div>
                          <span><small>Warehouse</small><strong>{{ segment.warehouse || 'n/a' }}</strong></span>
                          <span><small>Rows</small><strong>{{ segment.row_count }}</strong></span>
                          <span><small>Weight</small><strong>{{ segment.weight }}</strong></span>
                        </div>
                      </article>
                    </div>
                  </section>

                  <section class="asn-blind-reconciliation" *ngIf="group.reconciliation as reconciliation">
                    <div class="asn-merge-plan-head">
                      <div>
                        <span>Blind-trip reconciliation</span>
                        <strong>{{ reconciliation.status === 'matched' ? 'Existing blind trip found: ' + reconciliation.trip_number : 'Barcode matches need review' }}</strong>
                      </div>
                      <b>{{ reconciliation.status === 'matched' ? 'Automatic match' : 'Blocked' }}</b>
                    </div>
                    <ng-container *ngIf="reconciliation.status === 'matched'">
                      <p>{{ reconciliation.coverage_percent }}% of ASN labels match this trip. Process will update the existing trip and shipments and retain carton IDs, pallets, and scan state.</p>
                      <div class="asn-merge-plan-facts">
                        <span><small>Matched labels</small><strong>{{ reconciliation.matched_cartons }} / {{ reconciliation.source_cartons }}</strong></span>
                        <span><small>Expected, not scanned</small><strong>{{ reconciliation.missing_cartons }}</strong></span>
                        <span><small>Rows outside ASN</small><strong>{{ reconciliation.outside_asn_cartons }}</strong></span>
                        <span><small>Repeated rows</small><strong>{{ reconciliation.duplicate_excess_rows }}</strong></span>
                      </div>
                      <p>Missing labels become unreceived, unaudited cartons without pallets. Rows outside this ASN and repeated rows remain available for review; they are not removed or reassigned by this merge.</p>
                      <details *ngIf="reconciliation.missing?.length">
                        <summary>Expected labels not scanned ({{ reconciliation.missing_cartons }})</summary>
                        <div class="asn-reconciliation-labels"><code *ngFor="let carton of reconciliation.missing">{{ carton.store_number }} · {{ carton.barcode }}</code></div>
                      </details>
                      <details *ngIf="reconciliation.outside_asn?.length">
                        <summary>Scanned rows outside this ASN ({{ reconciliation.outside_asn_cartons }})</summary>
                        <div class="asn-reconciliation-labels"><code *ngFor="let carton of reconciliation.outside_asn">{{ carton.store_number }} · {{ carton.barcode }} · ASN {{ carton.asn_number || 'blank' }}</code></div>
                      </details>
                      <details *ngIf="reconciliation.duplicates?.length">
                        <summary>Repeated labels ({{ reconciliation.duplicate_excess_rows }} excess rows)</summary>
                        <div class="asn-reconciliation-labels"><code *ngFor="let carton of reconciliation.duplicates">{{ carton.barcode }} · {{ carton.product_uuids.length }} saved rows</code></div>
                      </details>
                      <details>
                        <summary>Metadata to merge</summary>
                        <pre class="asn-json asn-json--compact">{{ reconciliation.changes | json }}</pre>
                      </details>
                      <label class="asn-reconciliation-review">
                        <input type="checkbox" [checked]="isReconciliationReviewed(group)" (change)="reviewReconciliation(group, $any($event.target).checked)" [disabled]="processing || previewing">
                        <span>I reviewed these differences. Merge into trip {{ reconciliation.trip_number }} when I process this file.</span>
                      </label>
                    </ng-container>
                  </section>

                  <section class="asn-merge-plan" *ngIf="group.merge_plan as mergePlan" [class.is-enabled]="mergePlan.enabled">
                    <div class="asn-merge-plan-head">
                      <div>
                        <span>Backend merge plan</span>
                        <strong>{{ mergePlan.combined_store_groups > 0 ? 'Source order values grouped into authoritative shipments' : 'Shipment identity validated' }}</strong>
                      </div>
                      <b>Automatic</b>
                    </div>
                    <div class="asn-merge-plan-facts">
                      <span><small>Original order groups</small><strong>{{ mergePlan.original_order_groups }}</strong></span>
                      <span><small>Split order groups</small><strong>{{ mergePlan.split_order_groups }}</strong></span>
                      <span><small>Resulting shipments</small><strong>{{ mergePlan.resulting_store_groups }}</strong></span>
                      <span><small>Multi-order shipments</small><strong>{{ mergePlan.combined_store_groups }}</strong></span>
                    </div>
                    <p><i class="fa-solid fa-barcode"></i> This plan does not synthesize barcode values; it carries parsed, validated values while changing how rows are grouped into shipments.</p>
                  </section>

                  <div class="asn-group-notices" *ngIf="group.validation_errors?.length || group.warnings?.length">
                    <div class="asn-notice asn-notice--error" *ngFor="let error of group.validation_errors || []">
                      <i class="fa-solid fa-circle-xmark"></i>
                      <span>{{ error }}</span>
                    </div>
                    <div class="asn-notice asn-notice--warning" *ngFor="let warning of group.warnings || []">
                      <i class="fa-solid fa-triangle-exclamation"></i>
                      <span>{{ warning }}</span>
                    </div>
                  </div>

                  <details class="asn-map-details" open *ngIf="group.mapped_trip">
                    <summary>
                      <span>Trip Mapping JSON</span>
                      <i class="fa-solid fa-chevron-down"></i>
                    </summary>
                    <pre class="asn-json asn-json--compact">{{ group.mapped_trip | json }}</pre>
                  </details>

                  <div class="asn-store-table" *ngIf="group.stores?.length">
                    <div class="asn-store-row asn-store-row--head">
                      <span>Order / Sources</span>
                      <span>Store</span>
                      <span>BOL</span>
                      <span>Cartons</span>
                      <span>Weight</span>
                      <span>Store Match</span>
                      <span>Products</span>
                    </div>
                    <div class="asn-store-row" *ngFor="let store of group.stores || []; trackBy: trackByStore">
                      <span class="asn-store-orders">
                        <strong>{{ store.order_number }}</strong>
                        <small *ngIf="store.source_order_numbers && store.source_order_numbers.length > 1">Sources: {{ sourceOrdersLabel(store) }}</small>
                        <b *ngIf="store.combined">Combined</b>
                      </span>
                      <span>{{ store.store_number }}</span>
                      <span>{{ store.store_bol }}</span>
                      <span>{{ store.total_cartons }}</span>
                      <span>
                        {{ store.total_weight }}
                        <small *ngIf="store.weight_mode === 'repeated_shipment_total'">repeated shipment total used once</small>
                        <small *ngIf="store.weight_mode === 'summed_order_totals'">one total summed per source order</small>
                      </span>
                      <span><b class="asn-mini-status" [ngClass]="statusClass(store.lookup_status)">{{ store.lookup_status }}</b></span>
                      <span>{{ store.product_actions?.insert || 0 }} new / {{ store.product_actions?.update || 0 }} update</span>
                    </div>
                  </div>
                </article>
              </ng-container>

              <ng-template #noPreview>
                <div class="asn-empty asn-empty--large">
                  <i class="fa-solid fa-eye"></i>
                  <strong>No preview loaded.</strong>
                  <span>Select files and run preview to see mapping, validation, and JSON output.</span>
                </div>
              </ng-template>
            </section>

            <section *ngSwitchCase="'rows'" class="asn-tab-panel">
              <ng-container *ngIf="selectedParsedFile as rawFile; else noRows">
                <div class="asn-code-head">
                  <div class="min-w-0">
                    <div class="asn-eyebrow">Raw CSV Rows</div>
                    <h2 class="truncate">{{ rawFile.filename }}</h2>
                  </div>
                  <div class="asn-row-summary">
                    <span><strong>{{ filteredRawRowCount }}</strong> shown</span>
                    <span><strong>{{ rawFile.row_count }}</strong> total rows</span>
                    <span><strong>{{ selectedRawMasterBolCount }}</strong> Master BOLs</span>
                    <button type="button" class="asn-button asn-button--ghost asn-button--compact" (click)="downloadParsedFile(rawFile)">
                      <i class="fa-solid fa-download"></i>
                      <span>CSV</span>
                    </button>
                  </div>
                </div>

                <div class="asn-raw-filter-strip" *ngIf="rawMasterBolSummaries.length">
                  <button type="button" class="asn-raw-filter-chip" [class.is-active]="selectedRawMasterBol === 'all'" (click)="selectRawMasterBol('all')">
                    <strong>All</strong>
                    <span>{{ rawFile.row_count }} rows</span>
                  </button>
                  <button type="button" class="asn-raw-filter-chip" *ngFor="let summary of rawMasterBolSummaries; trackBy: trackByRawMasterBol" [class.is-active]="selectedRawMasterBol === summary.master_bol_number" [ngClass]="rawMasterBolClass(summary)" (click)="selectRawMasterBol(summary.master_bol_number)">
                    <strong>{{ summary.master_bol_number }}</strong>
                    <span>{{ summary.rows }} rows</span>
                    <small>{{ summary.spencer_rows }} Spencer / {{ summary.spirit_rows }} Spirit</small>
                  </button>
                </div>

                <div class="asn-raw-table-wrap" *ngIf="filteredRawRows.length; else noRowsInFile">
                  <table class="asn-raw-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th *ngFor="let column of selectedRowColumns">{{ column }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of filteredRawRows; let rowIndex = index">
                        <td>{{ rowIndex + 1 }}</td>
                        <td *ngFor="let column of selectedRowColumns">{{ rawCellValue(row, column) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <ng-template #noRowsInFile>
                  <div class="asn-empty asn-empty--large">
                    <i class="fa-solid fa-table-cells"></i>
                    <strong>No rows parsed.</strong>
                    <span>The file downloaded, but no data rows were parsed.</span>
                  </div>
                </ng-template>
              </ng-container>

              <ng-template #noRows>
                <div class="asn-empty asn-empty--large">
                  <i class="fa-solid fa-table-cells"></i>
                  <strong>No rows loaded.</strong>
                  <span>Select a file and run Preview to load raw CSV rows.</span>
                </div>
              </ng-template>
            </section>

            <section *ngSwitchCase="'json'" class="asn-tab-panel">
              <div class="asn-code-head">
                <div>
                  <div class="asn-eyebrow">Preview Payload</div>
                  <h2>JSON View</h2>
                </div>
                <span *ngIf="previewGeneratedAt">Generated {{ formatDate(previewGeneratedAt) }}</span>
              </div>
              <pre class="asn-json">{{ previewJson }}</pre>
            </section>

            <section *ngSwitchCase="'results'" class="asn-tab-panel">
              <div class="asn-code-head">
                <div>
                  <div class="asn-eyebrow">Import Run</div>
                  <h2>Results Disabled</h2>
                </div>
                <span *ngIf="processGeneratedAt">Processed {{ formatDate(processGeneratedAt) }}</span>
              </div>

              <div class="asn-results" *ngIf="processResults.length; else noResults">
                <article class="asn-result" *ngFor="let result of processResults; trackBy: trackByFilename">
                  <div class="asn-result-title">
                    <strong>{{ result.filename }}</strong>
                    <span class="asn-status-pill" [ngClass]="statusClass(result.status)">{{ result.status }}</span>
                  </div>

                  <div class="asn-message-row" *ngFor="let message of result.messages; trackBy: trackByMessage">
                    <span class="asn-status-dot" [ngClass]="statusClass(message.status)"></span>
                    <div>
                      <strong>{{ message.master_bol_number || message.status }}</strong>
                      <p>{{ message.message }}</p>
                      <small *ngIf="message.trip_number">Trip #{{ message.trip_number }}</small>
                    </div>
                  </div>
                </article>
              </div>

              <ng-template #noResults>
                <div class="asn-empty asn-empty--large">
                  <i class="fa-solid fa-list-check"></i>
                  <strong>Final import is disabled.</strong>
                  <span>Use Preview, Mapping, and JSON to check whether selected files are ready.</span>
                </div>
              </ng-template>

              <pre class="asn-json asn-json--compact" *ngIf="processResults.length">{{ processJson }}</pre>
            </section>
          </div>
        </section>
      </main>
    </div>
  </div>
</app-menu>
